"use client";

/**
 * StoreContext / AccountContext — активный аккаунт и магазин пользователя.
 *
 * Источник истины — БД (`sessions.current_account_id` / `current_store_id`).
 * Провайдер читает `/api/accounts/current` при mount и держит результат в
 * state. Переключение через `switchStore(id | null)` делает POST на бэк,
 * обновляет локальный state и запускает SWR-инвалидацию фоном, чтобы UI не
 * ждал тяжёлую перезагрузку таблиц. `switchAccount(id)` синхронизируется
 * полным fetchCurrent, потому что при смене аккаунта меняется больше контекста.
 *
 * SWR-инвалидация — селективная, по префиксам. Мы НЕ хотим ревалидировать
 * `/api/auth/me`, `/api/user-prefs`, `/api/templates` (они per-user или
 * глобальные). Ревалидируем только per-store и per-account ключи.
 *
 * Между вкладками одного браузера синхронизация через BroadcastChannel —
 * переключил в одной вкладке, в другой состояние обновится мгновенно.
 *
 * После переключения диспатчим window-event `frogstat:store-switched` — на
 * него подписан Sidebar (перезагружает список видов, т.к. он не на SWR).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { mutate } from "swr";
import { dataCacheIdentity } from "@/lib/api/data-cache-identity";
import { setApiWarmupIdentity } from "@/lib/api/browser-warmup";
import { clearTableResponses } from "@/lib/api/table-response-cache";
import { clearSupplyPlanSnapshotCache } from "@/lib/client/supply-plan-snapshot-cache";

export type Account = { id: string; name: string; plan: string; createdAt: number };
export type Store   = { id: string; accountId: string; name: string; marketplace: "ozon" | "wb"; orderIndex: number };

export type StoreMode = "single" | "all";

interface StoreState {
  user: { id: string; email: string | null; displayName?: string | null; role: string } | null;
  account: Account | null;
  store: Store | null;        // null = режим «Все магазины аккаунта»
  stores: Store[];            // все магазины текущего аккаунта
  mode: StoreMode;
  impersonating: boolean;
  loading: boolean;
}

export type StoreInitialData = Omit<StoreState, "loading"> | null;

interface StoreCtx extends StoreState {
  switchStore(storeId: string | null): Promise<void>;
  switchAccount(accountId: string): Promise<void>;
  refresh(): Promise<void>;
}

const StoreContext = createContext<StoreCtx | null>(null);

export function useStore(): StoreCtx {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
}

/** Anonymous page readers never join a real account session or switch stores.
 * The public token owns a separate client identity, including SWR cache keys. */
export function PublicWorkspaceStoreProvider({ token, store, children }: {
  token: string; store: { id: string; name: string; marketplace: "ozon" | "wb" }; children: React.ReactNode;
}) {
  const value = useMemo<StoreCtx>(() => {
    const identity = `public-page:${token}`;
    const blocked = async () => { throw new Error("Страница доступна только для просмотра"); };
    const selected: Store = { ...store, accountId: identity, orderIndex: 0 };
    return { user: { id: identity, email: null, role: "viewer" }, account: { id: identity, name: "Публичная страница", plan: "public", createdAt: 0 },
      store: selected, stores: [selected], mode: "single", impersonating: false, loading: false,
      switchStore: blocked, switchAccount: blocked, refresh: async () => {},
    };
  }, [token, store]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

const BROADCAST_CHANNEL = "frogstat:store-switch";

/**
 * Префиксы SWR-ключей которые зависят от активного store/account. При
 * переключении ревалидируем только их, чтобы не дёргать per-user и
 * глобальные endpoints (auth/me, user-prefs, templates, refresh).
 */
const STORE_DEPENDENT_KEY_PREFIXES = [
  "/api/products", "/api/inventory", "/api/orders", "/api/prices", "/api/cost-prices", "/api/finances",
  "/api/supplies", "/api/clusters", "/api/attributes", "/api/promotions",
  "/api/performance", "/api/photos", "/api/ab-tests", "/api/ads-config", "/api/status",
  "/api/explore",
  // per-account:
  "/api/views", "/api/notes", "/api/tasks", "/api/task-columns", "/api/favorites", "/api/ad-comments",
  // current-store state:
  "/api/refresh", "/api/settings",
];

const STORE_SUPPORT_KEY_PREFIXES = [
  "/api/photos", "/api/ads-config", "/api/status", "/api/explore",
  "/api/views", "/api/notes", "/api/tasks", "/api/task-columns", "/api/favorites", "/api/ad-comments",
  "/api/refresh", "/api/settings",
];

const STORE_SUPPORT_REVALIDATE_DELAY_MS = 4_000;

function keyStartsWithPrefixes(key: unknown, prefixes: string[]): boolean {
  if (typeof key === "string") {
    return prefixes.some(p => key.startsWith(p));
  }
  // SWR поддерживает массив/объект ключи — например ["/api/products", storeId].
  if (Array.isArray(key) && typeof key[0] === "string") {
    return prefixes.some(p => (key[0] as string).startsWith(p));
  }
  return false;
}

function isStoreDependent(key: unknown): boolean {
  return keyStartsWithPrefixes(key, STORE_DEPENDENT_KEY_PREFIXES);
}

function isStoreSupportDependent(key: unknown): boolean {
  return keyStartsWithPrefixes(key, STORE_SUPPORT_KEY_PREFIXES);
}

async function revalidateStoreKeys() {
  await mutate(isStoreDependent, undefined, { revalidate: true });
}

async function revalidateStoreSupportKeys() {
  await mutate(isStoreSupportDependent, undefined, { revalidate: true });
}

export function StoreProvider({
  children,
  initialData = null,
}: {
  children: React.ReactNode;
  initialData?: StoreInitialData;
}) {
  const hasInitialDataRef = useRef(initialData !== null);
  const [state, setState] = useState<StoreState>(() => initialData
    ? { ...initialData, loading: false }
    : {
        user: null,
        account: null,
        store: null,
        stores: [],
        mode: "single",
        impersonating: false,
        loading: true,
      });

  const bcRef = useRef<BroadcastChannel | null>(null);
  const supportRevalidateTimerRef = useRef<number | null>(null);

  const scheduleSupportRevalidation = useCallback(() => {
    if (supportRevalidateTimerRef.current !== null) {
      window.clearTimeout(supportRevalidateTimerRef.current);
    }
    supportRevalidateTimerRef.current = window.setTimeout(() => {
      supportRevalidateTimerRef.current = null;
      void revalidateStoreSupportKeys().catch(err => {
        console.warn("[store] delayed support revalidation failed:", err);
      });
    }, STORE_SUPPORT_REVALIDATE_DELAY_MS);
  }, []);

  const fetchCurrent = useCallback(async () => {
    try {
      const res = await fetch("/api/accounts/current", { cache: "no-store" });
      if (res.status === 401) {
        await Promise.all([clearTableResponses(), clearSupplyPlanSnapshotCache()]);
        // Сессия умерла — редиректим на логин. Иначе UI висит в «Нет аккаунта».
        // Не редиректим с /login и /register чтобы не закольцевать.
        if (typeof window !== "undefined") {
          const p = window.location.pathname;
          if (!p.startsWith("/login") && !p.startsWith("/register") && !p.startsWith("/shared/")) {
            window.location.href = `/login?from=${encodeURIComponent(p)}`;
            return;
          }
        }
        setState(s => ({ ...s, loading: false }));
        return;
      }
      if (!res.ok) {
        setState(s => ({ ...s, loading: false }));
        return;
      }
      const data = await res.json();
      setApiWarmupIdentity(dataCacheIdentity(data.user?.id, data.account?.id));
      setState({
        user: data.user,
        account: data.account,
        store: data.store,
        stores: data.stores ?? [],
        mode: data.mode,
        impersonating: data.impersonating === true,
        loading: false,
      });
    } catch {
      setState(s => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    setApiWarmupIdentity(dataCacheIdentity(state.user?.id, state.account?.id));
  }, [state.user?.id, state.account?.id]);

  useEffect(() => {
    if (!hasInitialDataRef.current) fetchCurrent();

    if (typeof BroadcastChannel !== "undefined") {
      const bc = new BroadcastChannel(BROADCAST_CHANNEL);
      bcRef.current = bc;
      bc.addEventListener("message", () => {
        // В другой вкладке store поменялся — синхронизируемся и ревалидируем.
        fetchCurrent();
        scheduleSupportRevalidation();
        try { window.dispatchEvent(new Event("frogstat:store-switched")); } catch {}
      });
      return () => {
        bc.close();
        bcRef.current = null;
      };
    }
  }, [fetchCurrent, scheduleSupportRevalidation]);

  useEffect(() => () => {
    if (supportRevalidateTimerRef.current !== null) {
      window.clearTimeout(supportRevalidateTimerRef.current);
      supportRevalidateTimerRef.current = null;
    }
  }, []);

  const notifyOtherTabs = useCallback(() => {
    try { bcRef.current?.postMessage({ ts: Date.now() }); } catch {}
  }, []);

  const switchStore = useCallback(async (storeId: string | null) => {
    const res = await fetch("/api/stores/switch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ storeId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error ?? "Не удалось переключить магазин");
    }
    setState(prev => {
      if (storeId === null) {
        return { ...prev, store: null, mode: "all", loading: false };
      }
      const nextStore = prev.stores.find(s => s.id === storeId);
      if (!nextStore) return prev;
      return { ...prev, store: nextStore, mode: "single", loading: false };
    });
    notifyOtherTabs();
    // Событие для не-SWR потребителей (Sidebar перезагружает виды).
    try { window.dispatchEvent(new Event("frogstat:store-switched")); } catch {}
    scheduleSupportRevalidation();
  }, [notifyOtherTabs, scheduleSupportRevalidation]);

  const switchAccount = useCallback(async (accountId: string) => {
    const res = await fetch("/api/accounts/switch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accountId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error ?? "Не удалось переключить аккаунт");
    }
    await fetchCurrent();
    notifyOtherTabs();
    await revalidateStoreKeys();
    try { window.dispatchEvent(new Event("frogstat:store-switched")); } catch {}
  }, [fetchCurrent, notifyOtherTabs]);

  // useMemo — без этого value пересобирается на каждом рендере StoreProvider'а,
  // все потребители (useStore в Sidebar/Cell/TopBar) получают лишний re-render.
  const value = useMemo<StoreCtx>(() => ({
    ...state,
    switchStore,
    switchAccount,
    refresh: fetchCurrent,
  }), [state, switchStore, switchAccount, fetchCurrent]);

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}
